import React, { useEffect, useState } from 'react'
import {Music, Play, FileText, ThumbsUp, X} from 'lucide-react'
import { motion } from 'framer-motion'
import { lumi } from '../lib/lumi'

const Worship: React.FC = () => {
  const [songs, setSongs] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedSong, setSelectedSong] = useState<any>(null)

  useEffect(() => {
    const fetchSongs = async () => {
      try {
        const response = await lumi.entities.worship_songs.list({
          sort: { createdAt: -1 },
          limit: 100
        })
        setSongs(response?.list || [])
      } catch (error) {
        console.error('Erro ao carregar louvores:', error)
        setSongs([])
      } finally {
        setLoading(false)
      }
    }
    fetchSongs()
  }, [])

  const getYouTubeEmbedUrl = (url: string) => {
    if (!url) return null
    
    try {
      // Regex universal para todos os formatos do YouTube
      const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/
      const match = url.match(regExp)
      const videoId = (match && match[7]?.length === 11) ? match[7] : null
      
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null
    } catch (error) {
      console.error('Erro ao processar URL do YouTube:', error)
      return null
    }
  }

  const handleWatchSong = (song: any) => {
    setSelectedSong(song)
  }

  const closeModal = () => {
    setSelectedSong(null)
  }

  // Mock data - mantido como fallback
  const mockSongs = [
    {
      id: '1',
      title: 'Como Agradecer',
      artist: 'REFUGE IN CHRIST MINISTRY',
      videoUrl: 'https://www.youtube.com/watch?v=...',
      lyrics: true,
      thumbnail: 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=400&h=250&fit=crop',
      recommendations: 45
    },
    {
      id: '2',
      title: 'Eu Me Rendo',
      artist: 'REFUGE IN CHRIST MINISTRY',
      videoUrl: 'https://www.youtube.com/watch?v=...',
      lyrics: true,
      thumbnail: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=250&fit=crop',
      recommendations: 38
    },
    {
      id: '3',
      title: 'Tua Graça Me Basta',
      artist: 'REFUGE IN CHRIST MINISTRY',
      videoUrl: 'https://www.youtube.com/watch?v=...',
      lyrics: true,
      thumbnail: 'https://images.unsplash.com/photo-1507676184212-d03ab07a01bf?w=400&h=250&fit=crop',
      recommendations: 52
    }
  ]

  const displaySongs = songs.length > 0 ? songs : mockSongs

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="bg-gradient-to-br from-[#1e3a8a] to-[#6b8f71] py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Music className="mx-auto mb-6 text-white" size={64} />
            <h1 className="text-5xl font-bold text-white mb-6">
              Louvores
            </h1>
            <p className="text-xl text-white/90">
              Biblioteca de músicas que adoram ao Senhor
            </p>
          </motion.div>
        </div>
      </section>

      {/* Lista de Louvores */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e3a8a]"></div>
            </div>
          ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {displaySongs.map((song, index) => (
              <motion.div
                key={song._id || song.id || index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all overflow-hidden group"
              >
                <div className="relative h-48 bg-gray-200 overflow-hidden">
                  <img 
                    src={song.thumbnailUrl || song.thumbnail || 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=400&h=250&fit=crop'} 
                    alt={song.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <button 
                      onClick={() => handleWatchSong(song)}
                      className="bg-white/90 w-16 h-16 rounded-full flex items-center justify-center hover:scale-110 transition-transform"
                    >
                      <Play className="text-[#1e3a8a] ml-1" size={28} />
                    </button>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-[#1e3a8a] mb-2">
                    {song.title}
                  </h3>
                  <p className="text-[#6b8f71] font-semibold mb-4">
                    {song.artist}
                  </p>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2 text-gray-600">
                      <ThumbsUp size={18} />
                      <span className="text-sm">{song.recommendations} indicações</span>
                    </div>
                    {song.lyrics && (
                      <button className="text-[#6b8f71] hover:text-[#5a7760] flex items-center gap-1 text-sm font-semibold">
                        <FileText size={18} />
                        Letra
                      </button>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => handleWatchSong(song)}
                      className="flex-1 bg-[#6b8f71] text-white py-2 rounded-lg font-semibold hover:bg-[#5a7760] transition-all text-sm"
                    >
                      Assistir
                    </button>
                    <button className="flex-1 bg-[#1e3a8a] text-white py-2 rounded-lg font-semibold hover:bg-[#162d6d] transition-all text-sm">
                      Indicar
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h3 className="text-3xl font-bold text-[#1e3a8a] mb-4">
              Indique Louvores
            </h3>
            <p className="text-gray-600 mb-6">
              Conhece alguma música que edifica e glorifica a Deus? Compartilhe conosco!
            </p>
            <button className="bg-[#6b8f71] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#5a7760] transition-all hover:scale-105 shadow-lg">
              Enviar Indicação
            </button>
          </motion.div>
        </div>
      </section>

      {/* Modal de Vídeo */}
      {selectedSong && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={closeModal}>
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl max-w-4xl w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-xl font-bold text-[#1e3a8a]">{selectedSong.title}</h3>
              <button 
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            <div className="aspect-video bg-black">
              {getYouTubeEmbedUrl(selectedSong.videoUrl || selectedSong.mediaUrl) ? (
                <iframe
                  width="100%"
                  height="100%"
                  src={getYouTubeEmbedUrl(selectedSong.videoUrl || selectedSong.mediaUrl) || ''}
                  title={selectedSong.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white">
                  <p>Vídeo não disponível</p>
                </div>
              )}
            </div>
            {selectedSong.lyrics && (
              <div className="p-4 max-h-60 overflow-y-auto">
                <h4 className="font-semibold text-[#1e3a8a] mb-2">Letra:</h4>
                <p className="text-gray-600 whitespace-pre-line">{selectedSong.lyrics}</p>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default Worship
